// src/pages/disputas/DisputasPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import {
  fetchDisputas,
  resolverDisputaThunk,
  cerrarDisputaThunk,
  cambiarEstadoDisputaThunk,
  setFiltroEstado,
  setPage,
  clearMessages,
} from "../../store/slices/disputasSlice";
import { Disputa, ResolverDisputaRequest } from "../../api/disputaApi";

// ── helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d?: string) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ESTADO_COLOR: Record<string, string> = {
  ABIERTA: "#f98115",
  EN_REVISION: "#3b82f6",
  RESUELTA: "#0cbb1d",
  CERRADA: "#888",
};

const ESTADOS = [
  { value: "", label: "Todas" },
  { value: "ABIERTA", label: "Abiertas" },
  { value: "EN_REVISION", label: "En revisión" },
  { value: "RESUELTA", label: "Resueltas" },
  { value: "CERRADA", label: "Cerradas" },
];

// ── Modal Resolver ─────────────────────────────────────────────────────────────

function ResolverModal({
  disputa,
  onConfirm,
  onClose,
  loading,
}: {
  disputa: Disputa;
  onConfirm: (req: ResolverDisputaRequest) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const [resolucion, setResolucion] = useState("");
  const [favorDe, setFavorDe] = useState<"CLIENTE" | "MVZ" | "NINGUNO">(
    "NINGUNO",
  );

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box" style={{ maxWidth: 520 }}>
        <div className="modal-title">
          <i
            className="bi bi-check-circle-fill"
            style={{ color: "var(--fg-green)" }}
          />
          Resolver disputa
        </div>

        <div
          style={{
            background: "#f8fafb",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
            fontSize: "0.875rem",
          }}
        >
          <div>
            <strong>Asunto:</strong> {disputa.asunto}
          </div>
          <div>
            <strong>Presentada por:</strong> {disputa.autorNombre} (
            {disputa.rolAutor})
          </div>
          <div>
            <strong>Servicio:</strong> {disputa.servicioNombre}
          </div>
          <div>
            <strong>Descripción:</strong> {disputa.descripcion}
          </div>
          {disputa.respuesta && (
            <div>
              <strong>Respuesta:</strong> {disputa.respuesta}
            </div>
          )}
        </div>

        <div className="mb-3">
          <label
            style={{
              fontWeight: 600,
              fontSize: "0.875rem",
              display: "block",
              marginBottom: 6,
            }}
          >
            Resolución del admin *
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Explica la decisión tomada..."
            value={resolucion}
            onChange={(e) => setResolucion(e.target.value)}
            style={{ borderRadius: 8, fontSize: "0.875rem" }}
          />
        </div>

        <div className="mb-3">
          <label
            style={{
              fontWeight: 600,
              fontSize: "0.875rem",
              display: "block",
              marginBottom: 8,
            }}
          >
            Fallo a favor de
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            {(["CLIENTE", "MVZ", "NINGUNO"] as const).map((v) => (
              <button
                key={v}
                type="button"
                style={{
                  flex: 1,
                  padding: "8px 0",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  border:
                    favorDe === v
                      ? "2px solid var(--fg-green)"
                      : "1.5px solid #e0e0e0",
                  background: favorDe === v ? "rgba(0,107,76,0.08)" : "white",
                  color: favorDe === v ? "var(--fg-green)" : "#555",
                  cursor: "pointer",
                }}
                onClick={() => setFavorDe(v)}
              >
                {v === "NINGUNO" ? "Ninguno (mediación)" : v}
              </button>
            ))}
          </div>
        </div>

        {/* Advertencia de impacto financiero según favorDe */}
        {favorDe === 'CLIENTE' && (
          <div style={{ background: '#fef9c3', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem', borderLeft: '3px solid #eab308' }}>
            <strong>⚠️ Impacto:</strong> El MVZ <strong>no podrá retirar</strong> el valor de este servicio.
            El saldo quedará permanentemente bloqueado en su billetera hasta ajuste manual.
          </div>
        )}
        {favorDe === 'MVZ' && (
          <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem', borderLeft: '3px solid #22c55e' }}>
            <strong>✅ Impacto:</strong> El MVZ podrá retirar normalmente el valor de este servicio.
          </div>
        )}
        {favorDe === 'NINGUNO' && (
          <div style={{ background: '#f0f9ff', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem', borderLeft: '3px solid #38bdf8' }}>
            <strong>ℹ️ Impacto:</strong> Mediación — sin bloqueo financiero. El MVZ puede retirar normalmente.
          </div>
        )}

        <div className="modal-actions">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="btn-fg-primary"
            onClick={() => onConfirm({ resolucion, favorDe })}
            disabled={loading || !resolucion.trim()}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />{" "}
                Procesando...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1" /> Confirmar resolución
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal Cerrar ───────────────────────────────────────────────────────────────

function CerrarModal({
  disputa,
  onConfirm,
  onClose,
  loading,
}: {
  disputa: Disputa;
  onConfirm: (motivo: string) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const [motivo, setMotivo] = useState("");
  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box">
        <div className="modal-title">
          <i
            className="bi bi-x-circle-fill"
            style={{ color: "var(--fg-red)" }}
          />
          Cerrar disputa sin resolución
        </div>
        <p
          style={{
            fontSize: "0.875rem",
            color: "var(--fg-muted)",
            marginBottom: 16,
          }}
        >
          Esta acción cierra la disputa sin determinar un ganador. Las partes
          serán notificadas.
        </p>
        <textarea
          className="form-control"
          rows={2}
          placeholder="Motivo del cierre (opcional)..."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          style={{ borderRadius: 8, fontSize: "0.875rem", marginBottom: 16 }}
        />
        <div className="modal-actions">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className="btn-fg-danger"
            onClick={() => onConfirm(motivo)}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-x-lg me-1" /> Cerrar disputa
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal En Revisión ─────────────────────────────────────────────────────────

function EnRevisionModal({
  disputa,
  onConfirm,
  onClose,
  loading,
}: {
  disputa: Disputa;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
}) {
  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="modal-title">
          <i className="bi bi-eye-fill" style={{ color: "#3b82f6" }} />
          Poner en revisión
        </div>
        <p
          style={{
            fontSize: "0.875rem",
            color: "var(--fg-muted)",
            marginBottom: 16,
          }}
        >
          Al marcar esta disputa como <strong>En Revisión</strong>, se enviará
          una notificación push al cliente y al MVZ indicando que la disputa
          está siendo revisada por los administradores.
        </p>
        <div
          style={{
            background: "#eff6ff",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
            fontSize: "0.875rem",
          }}
        >
          <div>
            <strong>Asunto:</strong> {disputa.asunto}
          </div>
          <div>
            <strong>Presentada por:</strong> {disputa.autorNombre} (
            {disputa.rolAutor})
          </div>
        </div>
        <div className="modal-actions">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="btn-fg-primary"
            style={{ background: "#3b82f6", borderColor: "#3b82f6" }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />{" "}
                Procesando...
              </>
            ) : (
              <>
                <i className="bi bi-eye me-1" /> Confirmar En Revisión
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────

export default function DisputasPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate(); // FIX: import correcto arriba
  const {
    items,
    loading,
    actionLoading,
    error,
    successMsg,
    filtroEstado,
    currentPage,
    totalPages,
    totalElements,
  } = useAppSelector((s) => s.disputas);

  const [modalResolver, setModalResolver] = useState<Disputa | null>(null);
  const [modalCerrar, setModalCerrar] = useState<Disputa | null>(null);
  const [modalEnRevision, setModalEnRevision] = useState<Disputa | null>(null);
  // FIX: eliminado modalDetalle — el botón "Ver" navega a /disputas/:id

  const load = useCallback(() => {
    dispatch(
      fetchDisputas({ estado: filtroEstado || undefined, page: currentPage }),
    );
  }, [dispatch, filtroEstado, currentPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (successMsg || error) {
      const t = setTimeout(() => dispatch(clearMessages()), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg, error, dispatch]);

  const handleResolver = async (req: ResolverDisputaRequest) => {
    if (!modalResolver) return;
    await dispatch(
      resolverDisputaThunk({ id: modalResolver.id, request: req }),
    );
    setModalResolver(null);
  };

  const handleEnRevision = async (id: string) => {
    await dispatch(
      cambiarEstadoDisputaThunk({ id, nuevoEstado: "EN_REVISION" as any }),
    );
    setModalEnRevision(null);
    load();
  };

  const handleCerrar = async (motivo: string) => {
    if (!modalCerrar) return;
    await dispatch(cerrarDisputaThunk({ id: modalCerrar.id, motivo }));
    setModalCerrar(null);
  };

  return (
    <>
      {/* Header */}
      <div className="page-header d-flex align-items-start justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">
            <i
              className="bi bi-shield-exclamation me-2"
              style={{ color: "var(--fg-orange)", fontSize: "1.2rem" }}
            />
            Disputas e inconformidades
          </h1>
          <p className="page-subtitle">
            Gestiona las inconformidades presentadas por clientes y usuarios MVZ
            sobre servicios veterinarios.
          </p>
        </div>
        <button className="btn-fg-primary" onClick={load}>
          <i className="bi bi-arrow-clockwise" /> Actualizar
        </button>
      </div>

      {/* Alertas */}
      {successMsg && (
        <div
          className="alert alert-success d-flex align-items-center gap-2 mb-3"
          style={{ borderRadius: 10 }}
        >
          <i className="bi bi-check-circle-fill" /> {successMsg}
        </div>
      )}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center gap-2 mb-3"
          style={{ borderRadius: 10 }}
        >
          <i className="bi bi-exclamation-triangle-fill" /> {error}
        </div>
      )}

      {/* Filtros */}
      <div className="fg-card mb-3">
        <div style={{ padding: "14px 20px" }}>
          <div className="filter-bar">
            <span
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "var(--fg-muted)",
              }}
            >
              <i className="bi bi-funnel me-1" /> Filtrar:
            </span>
            {ESTADOS.map((e) => (
              <button
                key={e.value}
                className={
                  filtroEstado === e.value ? "btn-fg-primary" : "btn-fg-outline"
                }
                style={{ padding: "6px 14px", fontSize: "0.82rem" }}
                onClick={() => dispatch(setFiltroEstado(e.value))}
              >
                {e.label}
              </button>
            ))}
            <span className="ms-auto text-muted-fg">
              {totalElements} resultado{totalElements !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="fg-card">
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: 200 }}
          >
            <div
              className="spinner-border"
              style={{ color: "var(--fg-green)" }}
            />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i
              className="bi bi-shield-check"
              style={{ color: "var(--fg-green)" }}
            />
            <p>No hay disputas con el filtro seleccionado</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="fg-table">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Asunto</th>
                    <th>Presentada por</th>
                    <th>Servicio</th>
                    <th>Fecha apertura</th>
                    <th>Respuesta</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => {
                    const color = ESTADO_COLOR[d.estado] ?? "#888";
                    const isLoading = actionLoading === d.id;
                    return (
                      <tr key={d.id}>
                        <td>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "4px 10px",
                              borderRadius: 20,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              background: color + "22",
                              color,
                            }}
                          >
                            <i
                              className={`bi ${
                                d.estado === "ABIERTA"
                                  ? "bi-hourglass-split"
                                  : d.estado === "EN_REVISION"
                                    ? "bi-eye"
                                    : d.estado === "RESUELTA"
                                      ? "bi-check-circle"
                                      : "bi-x-circle"
                              }`}
                            />
                            {d.estado}
                          </span>
                        </td>
                        <td>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              maxWidth: 200,
                              cursor: "pointer",
                            }}
                            title={d.asunto}
                            onClick={() => navigate(`/disputas/${d.id}`)}
                          >
                            {d.asunto.length > 40
                              ? d.asunto.slice(0, 40) + "..."
                              : d.asunto}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: "0.85rem" }}>
                            {d.autorNombre}
                          </div>
                          <div
                            style={{
                              fontSize: "0.72rem",
                              color: "var(--fg-muted)",
                            }}
                          >
                            {d.rolAutor === "CLIENTE" ? "👤 Cliente" : "🩺 MVZ"}
                          </div>
                        </td>
                        <td style={{ fontSize: "0.82rem" }}>
                          {d.servicioNombre || "—"}
                        </td>
                        <td
                          style={{
                            fontSize: "0.8rem",
                            color: "var(--fg-muted)",
                          }}
                        >
                          {formatDate(d.fechaApertura)}
                        </td>
                        <td>
                          {d.respuesta ? (
                            <span
                              style={{
                                color: "var(--fg-green)",
                                fontSize: "0.8rem",
                              }}
                            >
                              <i className="bi bi-check-circle me-1" /> Sí
                            </span>
                          ) : (
                            <span
                              style={{
                                color: "var(--fg-muted)",
                                fontSize: "0.8rem",
                              }}
                            >
                              No
                            </span>
                          )}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: 5,
                              flexWrap: "wrap",
                            }}
                          >
                            {/* FIX: navega a página de detalle en lugar de abrir modal */}
                            <button
                              className="btn-fg-outline"
                              style={{
                                padding: "5px 10px",
                                fontSize: "0.78rem",
                              }}
                              onClick={() => navigate(`/disputas/${d.id}`)}
                            >
                              <i className="bi bi-eye me-1" /> Ver detalle
                            </button>

                            {(d.estado === "ABIERTA" ||
                              d.estado === "EN_REVISION") && (
                              <>
                                {d.estado === "ABIERTA" && (
                                  <button
                                    className="btn-fg-outline"
                                    style={{
                                      padding: "5px 10px",
                                      fontSize: "0.78rem",
                                      color: "#3b82f6",
                                      borderColor: "#3b82f6",
                                    }}
                                    onClick={() => setModalEnRevision(d)}
                                    disabled={isLoading}
                                  >
                                    <i className="bi bi-eye me-1" /> En revisión
                                  </button>
                                )}
                                <button
                                  className="btn-fg-success"
                                  style={{
                                    padding: "5px 10px",
                                    fontSize: "0.78rem",
                                  }}
                                  onClick={() => setModalResolver(d)}
                                  disabled={isLoading}
                                >
                                  {isLoading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                  ) : (
                                    <>
                                      <i className="bi bi-check-lg" /> Resolver
                                    </>
                                  )}
                                </button>
                                <button
                                  className="btn-fg-danger"
                                  style={{
                                    padding: "5px 10px",
                                    fontSize: "0.78rem",
                                  }}
                                  onClick={() => setModalCerrar(d)}
                                  disabled={isLoading}
                                >
                                  <i className="bi bi-x-lg" /> Cerrar
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="pagination-bar">
              <span>
                Página {currentPage + 1} de {Math.max(totalPages, 1)} ·{" "}
                {totalElements} disputas
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 0}
                  onClick={() => dispatch(setPage(currentPage - 1))}
                  style={{ borderRadius: 6 }}
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => dispatch(setPage(currentPage + 1))}
                  style={{ borderRadius: 6 }}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modales — solo acciones admin (resolver, cerrar, en revisión) */}
      {modalResolver && (
        <ResolverModal
          disputa={modalResolver}
          onConfirm={handleResolver}
          onClose={() => setModalResolver(null)}
          loading={actionLoading === modalResolver.id}
        />
      )}
      {modalCerrar && (
        <CerrarModal
          disputa={modalCerrar}
          onConfirm={handleCerrar}
          onClose={() => setModalCerrar(null)}
          loading={actionLoading === modalCerrar.id}
        />
      )}
      {modalEnRevision && (
        <EnRevisionModal
          disputa={modalEnRevision}
          onConfirm={() => handleEnRevision(modalEnRevision.id)}
          onClose={() => setModalEnRevision(null)}
          loading={actionLoading === modalEnRevision.id}
        />
      )}
    </>
  );
}
